import { useEffect, useState } from 'react';
import type { MemberBooking, MemberBookingsResponse } from '@secretsoverflow/types';
import { api } from '../../lib/api';
import { FormNotice } from './MemberForm';
import { messageFrom } from './memberErrors';

/**
 * The member's own stays.
 *
 * Loaded here rather than in the account page's auth context: nothing else on
 * the site needs it, and a list that can grow unbounded should not be dragged
 * along by every profile read.
 */

const money = (cents: number, currency: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(
    cents / 100,
  );

/**
 * Stay dates are plain YYYY-MM-DD with no time or zone. Parsing them as UTC noon
 * keeps them on the intended day: `new Date('2026-08-15')` is midnight UTC,
 * which formats as the 14th anywhere west of Greenwich — exactly the sort of
 * off-by-one-day that makes a guest think we booked the wrong night.
 */
const stayDate = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
};

/** When the booking was made. A timestamp, so it is shown in the reader's zone. */
const bookedOn = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const STATUS_LABEL: Record<MemberBooking['status'], string> = {
  confirmed: 'Confirmed',
  // Deliberately not "Paid": from the guest's side the money has gone and the
  // room is not theirs yet, and "Paid" reads as done.
  paid: 'Awaiting confirmation',
  cancelled: 'Cancelled',
};

function BookingRow({ booking }: { booking: MemberBooking }) {
  const reference = booking.confirmationCode ?? booking.vendorBookingId;
  const booked = bookedOn(booking.bookedAt);
  return (
    <li className={`booking-item booking-${booking.status}`}>
      <div className="booking-head">
        <div>
          <h5 className="booking-hotel">{booking.hotelName}</h5>
          {booking.hotelCity ? <p className="booking-city">{booking.hotelCity}</p> : null}
        </div>
        <span className={`booking-status booking-status-${booking.status}`}>
          {STATUS_LABEL[booking.status]}
        </span>
      </div>

      <dl className="booking-facts">
        <div>
          <dt>Check in</dt>
          <dd>{stayDate(booking.checkIn)}</dd>
        </div>
        <div>
          <dt>Check out</dt>
          <dd>{stayDate(booking.checkOut)}</dd>
        </div>
        <div>
          <dt>Nights</dt>
          <dd>{booking.nights}</dd>
        </div>
        <div>
          <dt>Guests</dt>
          <dd>{booking.guests}</dd>
        </div>
        <div>
          <dt>Room</dt>
          <dd>{booking.roomType}</dd>
        </div>
        <div>
          <dt>Total</dt>
          <dd>{money(booking.totalPriceCents, booking.currency)}</dd>
        </div>
        {reference ? (
          <div>
            <dt>Confirmation</dt>
            <dd className="booking-ref">{reference}</dd>
          </div>
        ) : null}
        {booked ? (
          <div>
            <dt>Booked</dt>
            <dd>{booked}</dd>
          </div>
        ) : null}
      </dl>

      {booking.roomDescription ? (
        <p className="booking-note">{booking.roomDescription}</p>
      ) : null}
      {booking.refundable === true ? (
        <p className="booking-note booking-refundable">Free cancellation</p>
      ) : null}
      {booking.refundable === false ? (
        <p className="booking-note">Non-refundable</p>
      ) : null}
    </li>
  );
}

export function MemberBookingsCard() {
  const [data, setData] = useState<MemberBookingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        setData(await api.member.bookings(controller.signal));
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(messageFrom(err, 'We could not load your bookings.'));
      }
    })();
    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <div className="member-card">
        <h3>Your bookings</h3>
        <FormNotice kind="error">{error}</FormNotice>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="member-card">
        <h3>Your bookings</h3>
        <p className="po-muted">Loading your bookings…</p>
      </div>
    );
  }

  const { upcoming, past } = data;

  if (!upcoming.length && !past.length) {
    return (
      <div className="member-card">
        <h3>Your bookings</h3>
        <p className="po-muted">
          You have no bookings yet. When Secrets Hideaway is full, <a href="/book">find a room
          nearby</a> and it will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="member-card">
      <h3>Your bookings</h3>

      {upcoming.length ? (
        <section className="booking-group">
          <h4 className="booking-group-title">Upcoming</h4>
          <ul className="booking-list">
            {upcoming.map((b) => (
              <BookingRow key={b.id} booking={b} />
            ))}
          </ul>
        </section>
      ) : null}

      {past.length ? (
        <section className="booking-group">
          <h4 className="booking-group-title">Past</h4>
          <ul className="booking-list">
            {past.map((b) => (
              <BookingRow key={b.id} booking={b} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
